import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Paperclip, Download, ExternalLink, X, FileText, Loader2 } from "lucide-react";

export const LEAD_BRIEF_NOTE = "Lead brief attachment";

export async function uploadLeadBriefFiles(leadId: string, files: File[]) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in");
  for (const file of files) {
    const ext = file.name.includes(".") ? file.name.split(".").pop() : "bin";
    const filePath = `${user.id}/${crypto.randomUUID()}.${ext}`;
    const { error: upErr } = await supabase.storage.from("documents").upload(filePath, file, { contentType: file.type || undefined });
    if (upErr) throw upErr;
    const { error: dbErr } = await supabase.from("documents").insert({
      lead_id: leadId,
      user_id: user.id,
      file_name: file.name,
      file_path: filePath,
      file_type: file.type || "application/octet-stream",
      file_size: file.size,
      notes: LEAD_BRIEF_NOTE,
    } as any);
    if (dbErr) throw dbErr;
  }
}

const fmtSize = (b: number) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

interface Props {
  leadId?: string | null;
  pendingFiles?: File[];
  setPendingFiles?: (f: File[]) => void;
}

export function LeadBriefAttachments({ leadId, pendingFiles = [], setPendingFiles }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const live = !!leadId;

  const { data: existing = [] } = useQuery({
    queryKey: ["lead-brief-attachments", leadId],
    enabled: live,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("documents")
        .select("id, file_name, file_path, file_type, file_size")
        .eq("lead_id", leadId!)
        .eq("notes", LEAD_BRIEF_NOTE)
        .order("created_at");
      if (error) throw error;
      return data || [];
    },
  });

  const addFiles = async (files: File[]) => {
    if (!files.length) return;
    const named = files.map((f) =>
      f.name && f.name !== "image.png" ? f : new File([f], `screenshot-${Date.now()}.${(f.type.split("/")[1] || "png")}`, { type: f.type })
    );
    if (!live) {
      setPendingFiles?.([...pendingFiles, ...named]);
      return;
    }
    setBusy(true);
    try {
      await uploadLeadBriefFiles(leadId!, named);
      qc.invalidateQueries({ queryKey: ["lead-brief-attachments", leadId] });
      qc.invalidateQueries({ queryKey: ["documents"] });
      toast({ title: "Attached", description: `${named.length} file(s) added to the lead's documents` });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const removeExisting = async (doc: any) => {
    await supabase.storage.from("documents").remove([doc.file_path]);
    const { error } = await supabase.from("documents").delete().eq("id", doc.id);
    if (error) return toast({ title: "Could not remove", description: error.message, variant: "destructive" });
    qc.invalidateQueries({ queryKey: ["lead-brief-attachments", leadId] });
  };

  const allPaths = existing.map((d: any) => d.file_path);
  const { data: thumbs = {} } = useQuery({
    queryKey: ["lead-brief-thumbs", leadId, allPaths.join("|")],
    enabled: live && allPaths.length > 0,
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.storage.from("documents").createSignedUrls(allPaths, 3600);
      const map: Record<string, string> = {};
      (data || []).forEach((r: any) => { if (r.signedUrl && r.path) map[r.path] = r.signedUrl; });
      return map;
    },
  });
  const [viewing, setViewing] = useState<any>(null);
  const openExisting = (doc: any) => setViewing(doc);
  const viewUrl: string | undefined = viewing ? (thumbs as any)[viewing.file_path] : undefined;

  const items = [
    ...existing.map((d: any) => ({ key: d.id, name: d.file_name, size: d.file_size, type: d.file_type, preview: (d.file_type || "").startsWith("image/") ? (thumbs as any)[d.file_path] : undefined, onOpen: () => openExisting(d), onRemove: () => removeExisting(d) })),
    ...pendingFiles.map((f, i) => ({ key: `p${i}`, name: f.name, size: f.size, type: f.type, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : undefined, onOpen: undefined, onRemove: () => setPendingFiles?.(pendingFiles.filter((_, j) => j !== i)) })),
  ];

  return (
    <div
      tabIndex={0}
      onPaste={(e) => {
        const files = Array.from(e.clipboardData.files);
        if (files.length) { e.preventDefault(); addFiles(files); }
      }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(Array.from(e.dataTransfer.files)); }}
      className={`mt-2 rounded-md border border-dashed p-3 text-sm outline-none focus:ring-2 focus:ring-ring ${dragOver ? "border-primary bg-primary/5" : "border-border"}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Paperclip className="h-4 w-4 mr-1" />}
          Attach files
        </Button>
        <span className="text-xs text-muted-foreground">Screenshots, PDFs, documents — drag here or click and paste</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
        onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
      />
      {items.length > 0 && (
        <ul className="mt-3 space-y-2">
          {items.map((it: any) => (
            <li key={it.key} className="flex items-center gap-2 min-w-0">
              {it.preview ? (
                <img src={it.preview} alt="" className="h-8 w-8 rounded object-cover shrink-0" />
              ) : (
                <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
              )}
              <button type="button" onClick={it.onOpen} disabled={!it.onOpen} className="truncate text-left flex-1 min-w-0 enabled:hover:underline">
                {it.name}
              </button>
              <span className="text-xs text-muted-foreground shrink-0">{fmtSize(it.size || 0)}</span>
              <Button type="button" variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={it.onRemove} aria-label="Remove">
                <X className="h-3 w-3" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader><DialogTitle className="truncate pr-6">{viewing?.file_name}</DialogTitle></DialogHeader>
          <div className="flex-1 min-h-0 overflow-auto flex items-center justify-center">
            {!viewUrl ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : (viewing.file_type || "").startsWith("image/") ? (
              <img src={viewUrl} alt={viewing.file_name} className="max-w-full max-h-[70vh] object-contain" />
            ) : viewing.file_type === "application/pdf" ? (
              <iframe src={viewUrl} title={viewing.file_name} className="w-full h-[70vh] rounded border" />
            ) : (
              <p className="text-sm text-muted-foreground">Preview not available — use Download.</p>
            )}
          </div>
          {viewUrl && (
            <div className="flex flex-wrap justify-end gap-2">
              <Button asChild variant="outline" size="sm"><a href={viewUrl} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4 mr-1" />Open in new tab</a></Button>
              <Button asChild size="sm"><a href={`${viewUrl}&download=${encodeURIComponent(viewing.file_name)}`}><Download className="h-4 w-4 mr-1" />Download</a></Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
